import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { Checkbox, PriceRangePicker, Radio, Button } from '@dreamtree-org/twreact-ui';
import { WARRANTY_OPTIONS, toggleValue, setFilter } from '../lib/filters';
import { shopHref } from '../lib/shop';

const rupees = (n) => `₹${Number(n).toLocaleString('en-IN')}`;

/**
 * WEB-02 filter controls. Stateless: reads `f`, reports the next filter object via onChange.
 * `type` is the product type being browsed (null on /shop, which offers `types` to pick from).
 * `only` renders one group without the heading (a filter bar's popover): see filterGroups().
 */
export default function FilterPanel({ f, fc, onChange, onClear, type = null, types = [], only = null }) {
  const show = (key) => !only || only === key;
  const groups = facetGroups(fc);
  // Only options some unit in stock has (MERCH-57); a selected one stays so it can be undone.
  // The type's collections (registry `collections`); none for a type that declares none.
  const collections = (fc.collections || []).length ? keepSelected(fc.cat || [], f.cat, fc.collections) : [];
  const warranties = keepSelected(fc.warranty || [], f.warranty);
  const shownTypes = types.filter((t) => t.count > 0);
  const lo = fc.priceMin;
  const hi = fc.priceMax > fc.priceMin ? fc.priceMax : fc.priceMin + 1000;
  const [range, setRange] = useState({ min: Number(f.min) || lo, max: Number(f.max) || hi });
  useEffect(() => setRange({ min: Number(f.min) || lo, max: Number(f.max) || hi }), [f.min, f.max, lo, hi]);

  const step = 500;
  const mid1 = Math.round((lo + (hi - lo) / 3) / step) * step;
  const mid2 = Math.round((lo + ((hi - lo) * 2) / 3) / step) * step;
  const presets = hi - lo >= 3 * step
    ? [
        { id: 'p1', label: `Under ${rupees(mid1)}`, value: { min: lo, max: mid1 } },
        { id: 'p2', label: `${rupees(mid1)} – ${rupees(mid2)}`, value: { min: mid1, max: mid2 } },
        { id: 'p3', label: `Over ${rupees(mid2)}`, value: { min: mid2, max: hi } },
      ]
    : [];

  const commitPrice = (r) => onChange({ ...f, min: r.min > lo ? String(r.min) : '', max: r.max < hi ? String(r.max) : '', page: 1 });

  return (
    <div className="space-y-5 text-sm">
      {only ? null : (
        <div className="flex items-center justify-between">
          <h2 className="text-base font-semibold">Filters</h2>
          <Button size="xs" variant="ghost" onClick={onClear}>Clear all</Button>
        </div>
      )}

      {show('types') && shownTypes.length > 1 ? (
        <fieldset className="space-y-2">
          <legend className="mb-1 font-semibold">Product type</legend>
          <div className="flex flex-wrap gap-1.5">
            {shownTypes.map((t) => (
              <Link key={t.key} to={shopHref(t)} className="inline-flex min-h-9 items-center rounded-full border border-[var(--app-border)] px-3 text-xs hover:border-primary-600">
                {t.plural} ({t.count})
              </Link>
            ))}
          </div>
        </fieldset>
      ) : null}

      {show('cat') && type && collections.length ? (
        <fieldset className="space-y-2">
          <legend className="mb-1 font-semibold">Collections</legend>
          <div className="flex flex-wrap gap-1.5">
            {collections.map((c) => (
              <Button key={c.value} size="xs" variant={f.cat === c.value ? 'primary' : 'outline'} onClick={() => onChange(setFilter(f, 'cat', f.cat === c.value ? '' : c.value))} aria-pressed={f.cat === c.value}>
                {c.label}
              </Button>
            ))}
          </div>
        </fieldset>
      ) : null}

      {show('price') ? (
      <fieldset className="space-y-2">
        <legend className="mb-1 font-semibold">Price</legend>
        <PriceRangePicker
          min={lo}
          max={hi}
          step={step}
          minGap={step}
          value={range}
          onChange={setRange}
          onFinalChange={commitPrice}
          formatValue={rupees}
          colorClass="bg-primary-600"
          size="sm"
          ariaLabel="Price range in rupees"
          className="p-0"
          showInputs={false}
        />
        {/* Presets rendered here: the picker's own presets share the inputs' row and overflow narrow sidebars. */}
        {presets.length ? (
          <div className="flex flex-wrap gap-1.5 pt-1">
            {presets.map((p) => {
              const on = Number(f.min || lo) === p.value.min && Number(f.max || hi) === p.value.max;
              return (
                <Button key={p.id} size="xs" variant={on ? 'primary' : 'outline'} aria-pressed={on} onClick={() => commitPrice(p.value)}>{p.label}</Button>
              );
            })}
          </div>
        ) : null}
        <p className="text-xs opacity-70">{rupees(range.min)} – {rupees(range.max)}</p>
      </fieldset>
      ) : null}

      {groups.map(([key, label]) =>
        show(key) && fc[key] && fc[key].length ? (
          <fieldset key={key} className="space-y-1">
            <legend className="mb-1 font-semibold">{label}</legend>
            {fc[key].map((o) => (
              <div key={o.value} className="flex min-h-9 items-center">
                <Checkbox size="sm" label={`${o.label} (${o.count})`} checked={(f[key] || []).includes(o.value)} onChange={() => onChange(toggleValue(f, key, o.value))} />
              </div>
            ))}
          </fieldset>
        ) : null
      )}

      {show('warranty') && warranties.length ? (
      <fieldset className="space-y-1">
        <legend className="mb-1 font-semibold">Warranty</legend>
        {[ANY_WARRANTY, ...warranties].map((o) => (
          <div key={o.value || 'any'} className="flex min-h-9 items-center">
            <Radio name="warranty" size="sm" value={o.value} label={o.value ? `${o.label} (${o.count})` : o.label} checked={f.warranty === o.value} onChange={() => onChange(setFilter(f, 'warranty', o.value))} radioColor="var(--color-primary-600)" />
          </div>
        ))}
      </fieldset>
      ) : null}
    </div>
  );
}

const ANY_WARRANTY = WARRANTY_OPTIONS.find((o) => !o.value);

/** Options with units in stock, plus the selected value (by `value`) when it has none. */
function keepSelected(options, selected, labels = []) {
  if (!selected || options.some((o) => o.value === selected)) return options;
  return [...options, { value: selected, label: (WARRANTY_OPTIONS.find((o) => o.value === selected) || (labels || []).find((o) => o.value === selected) || { label: selected }).label, count: 0 }];
}

// Attribute groups are labelled by the registry (fc.groups: `filter_label`, else the attribute's label).
function facetGroups(fc) {
  return [
    ['brand', 'Brand'],
    ['category', 'Category'],
    ...(fc.groups || []).map((g) => [g.param, g.label]),
    ['grade', 'Condition grade'],
    ['condition', 'Condition'],
  ];
}

/**
 * The groups a filter bar shows, each with how many of its values are in use
 * ([{ key, label, active }]); only groups this catalog has options for.
 */
export function filterGroups(f, fc, type = null) {
  return [
    ...(type && (fc.collections || []).length && keepSelected(fc.cat || [], f.cat, fc.collections).length ? [{ key: 'cat', label: 'Collections', active: f.cat ? 1 : 0 }] : []),
    { key: 'price', label: 'Price', active: f.min || f.max ? 1 : 0 },
    ...facetGroups(fc).filter(([key]) => fc[key] && fc[key].length).map(([key, label]) => ({ key, label, active: (f[key] || []).length })),
    ...(keepSelected(fc.warranty || [], f.warranty).length ? [{ key: 'warranty', label: 'Warranty', active: f.warranty ? 1 : 0 }] : []),
  ];
}
